import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { colors } from '../../../../../packages/colors';
import { Button, btnSize, btnType } from '../../../../../packages/ui/atoms/button';
import { Icons } from '../../../../../packages/icons';
import { sx } from './RightPanel.styles';
import RecommendationsDefault from '../../../../../assets/images/our-recommendations.png';
import TranscriptionOff from '../../../../../assets/images/transcription.png';

/**
 * Base RightPanel container - provides consistent styling
 */
const RightPanel = ({ children, variant = 'default' }) => {
  const backgroundColor = variant === 'error' ? colors.destructive50 : colors.primary50;
  return <Box sx={{ ...sx.rightPanel, backgroundColor }}>{children}</Box>;
};

/**
 * Panel Header - Title with optional icon and count badge
 */
const PanelHeader = ({ icon, title, count }) => {
  return (
    <Box sx={sx.panelHeader}>
      <Box sx={sx.headerContent}>
        <Box display="flex" alignItems="center" gap="8px">
          {icon && (typeof icon === 'string' ? <Icons glyph={icon} size={20} /> : icon)}
          <Typography sx={sx.headerTitle}>{title}</Typography>
        </Box>
        {count !== undefined && count > 0 && <Box sx={sx.countBadge}>{count}</Box>}
      </Box>
    </Box>
  );
};

/**
 * Panel Body - Scrollable content area
 */
const PanelBody = ({ children }) => {
  return <Box sx={sx.panelBody}>{children}</Box>;
};

/**
 * Panel Footer - Action buttons area
 */
const PanelFooter = ({ children }) => {
  return <Box sx={sx.panelFooter}>{children}</Box>;
};

/**
 * Empty State - Default view when no recommendations
 */
const EmptyState = ({ openGiggle }) => {
  return (
    <Box sx={sx.centeredState}>
      <Box>
        <img src={RecommendationsDefault} alt="recommendations-default" width={120} height={120} />
      </Box>
      <Box paddingX="24px">
        <Typography sx={sx.stateDescription}>
          Know your dreams and let the universe conspire with you.
        </Typography>
      </Box>
      <Box>
        <Button
          disableRipple
          size={btnSize.SMALL}
          variant={btnType.TEXT}
          onClick={openGiggle}
          sx={sx.emptyStateButton}
        >
          Take a quick break
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Loading State - Waiting for AI
 */
const LoadingState = ({ onBackToTodos, message, description, onGiggle }) => {
  return (
    <Box sx={sx.centeredState}>
      <Box sx={sx.stateIcon}>
        <Icons glyph="ai-loading-lottie" />
      </Box>
      <Box paddingX="24px">
        <Typography sx={sx.stateTitle}>{message}</Typography>
        <Typography sx={sx.stateDescription}>{description}</Typography>
      </Box>
      {onBackToTodos && (
        <Button
          disableRipple
          size={btnSize.SMALL}
          variant={btnType.TEXT}
          onClick={onBackToTodos}
          sx={sx.emptyStateButton}
        >
          Back to Todos
        </Button>
      )}
      {onGiggle && (
        <Box display="flex" flexDirection="column" alignItems="center" gap="4px" marginTop="80px">
          <Typography sx={sx.giggleText}>Want a quick laugh?</Typography>
          <Button
            disableRipple
            size={btnSize.SMALL}
            variant={btnType.TEXT}
            onClick={onGiggle}
            sx={sx.emptyStateButton}
          >
            Induce giggle
          </Button>
        </Box>
      )}
    </Box>
  );
};

/**
 * Error State - Plan creation error
 */
const ErrorState = ({ onContactSupport }) => {
  return (
    <Box sx={sx.centeredState}>
      <Box sx={sx.stateIcon}>
        <Icons glyph="ai-loading-lottie" />
      </Box>
      <Box>
        <Typography sx={sx.stateTitle}>Plan creation is taking too long</Typography>
        <Typography sx={sx.stateDescription}>
          Plan creation is taking too long. Please contact support.
        </Typography>
      </Box>
      {onContactSupport && (
        <Button variant={btnType.TEXT} onClick={onContactSupport}>
          Contact support
        </Button>
      )}
    </Box>
  );
};

/**
 * Transcription Off State
 */
const TranscriptionOffState = () => {
  return (
    <Box sx={sx.centeredState}>
      <Box>
        <img src={TranscriptionOff} alt="transcription-off" />
      </Box>
      <Box>
        <Typography sx={sx.stateTitle}>Transcription is turned off</Typography>
        <Typography sx={sx.stateDescription}>Transcription is turned off for this Member -</Typography>
        <Typography sx={sx.stateDescription}>Please update the note yourself</Typography>
      </Box>
    </Box>
  );
};

// RecommendationsView and ReasoningView removed - will be implemented when needed for specific sections

// Export all components
export {
  RightPanel,
  PanelHeader,
  PanelBody,
  PanelFooter,
  EmptyState,
  LoadingState,
  ErrorState,
  TranscriptionOffState,
};

RightPanel.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf(['default', 'error']),
};

PanelHeader.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.string.isRequired,
  count: PropTypes.number,
};

PanelBody.propTypes = {
  children: PropTypes.node,
};

PanelFooter.propTypes = {
  children: PropTypes.node,
};

LoadingState.propTypes = {
  onBackToTodos: PropTypes.func,
};

ErrorState.propTypes = {
  onContactSupport: PropTypes.func,
};

EmptyState.propTypes = {
  openGiggle: PropTypes.func,
};
